import {html, HTMLTemplateResult, nothing} from "lit";
import {translate} from "lit-translate";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {classMap} from "lit/directives/class-map.js";
import {repeat} from "lit/directives/repeat.js";
import {styleMap} from "lit/directives/style-map.js";
import {until} from "lit/directives/until.js";
import {when} from "lit/directives/when.js";
import {DateTime, Interval} from "luxon";
import {container} from "tsyringe";
import {EmployeeToIssueAssignment} from "../../domain/entities/employee-to-issue-assignment.entity";
import {Issue} from "../../domain/entities/issue.entity";
import {Keyword} from "../../domain/entities/keyword.entity";
import {IssueHelper} from "../../domain/helpers/issue.helper";
import {IssueRepository} from "../../domain/repositories/issue.repository";
import {StringHelper} from "../../helpers/string.helper";
import {MessageBroker} from "../../message-broker/message-broker";
import {EntityChangedPayload} from "../../message-broker/payloads/entity-changed.payload";
import {EntityCreatedPayload} from "../../message-broker/payloads/entity-created.payload";
import {EntityDeletedPayload} from "../../message-broker/payloads/entity-deleted.payload";
import {TaskListQueryItem} from "../../types/task-list-query.item.type";
import {AbstractLitElement} from "../abstract-lit.element";
import {DateHelper} from "../../helpers/date.helper";
import {rootUidOf} from "../../domain/helpers/issue-type-root.helper";
import {itemColorStyle, resolveRecordTextColor} from "../../helpers/header-item-style.helper";

@customElement(`ha-task-list-item`)
export class HaTaskListItemComponent extends AbstractLitElement {

    @property({type: Boolean})
    public isFirstOfFuture = false;

    @property({type: Object})
    public item: TaskListQueryItem | null = null;

    private _messageBroker: MessageBroker = container.resolve(MessageBroker);

    @state()
    private data: Promise<Issue | null> | null = null;

    // Last resolved issue, used to match assignment-change events that only carry
    // the assignment uid (not its parent issue) against this item's issue.
    private _issue: Issue | null = null;

    // Set once this item's issue is deleted: the card stays in place (no layout
    // shift) but renders the `is-deleted` class so the styling can mark it as
    // removed. The card leaves the list on the next reload (filter/midnight).
    @state()
    private _deleted = false;

    public connectedCallback() {
        super.connectedCallback();
        this._messageBroker.registerListener<EntityChangedPayload<Issue>>(this, {
            messageType: `entity.changed`,
            bufferTime: 150,
            filterFn: message => message.payload.prototype === Issue && message.payload.uid === this.item?.uid,
            method: () => this.requestUpdate(),
            disconnect: this._disconnected$
        });

        // The issue's assignee/time-budget block reflects EmployeeToIssueAssignment
        // child entities. Those mutations publish events for the assignment, not for
        // the parent Issue, so re-render this item when an assignment of this issue
        // is created, updated or deleted.
        this._messageBroker.registerListener<EntityCreatedPayload<EmployeeToIssueAssignment>>(this, {
            messageType: `entity.created`,
            bufferTime: 150,
            filterFn: message => message.payload.prototype === EmployeeToIssueAssignment
                && (message.payload.entity as EmployeeToIssueAssignment).issue?.uid === this.item?.uid,
            method: () => this.requestUpdate(),
            disconnect: this._disconnected$
        });

        this._messageBroker.registerListener<EntityDeletedPayload<EmployeeToIssueAssignment>>(this, {
            messageType: `entity.deleted`,
            bufferTime: 150,
            filterFn: message => message.payload.entity instanceof EmployeeToIssueAssignment
                && message.payload.entity.issue?.uid === this.item?.uid,
            method: () => this.requestUpdate(),
            disconnect: this._disconnected$
        });

        this._messageBroker.registerListener<EntityChangedPayload<EmployeeToIssueAssignment>>(this, {
            messageType: `entity.changed`,
            bufferTime: 150,
            filterFn: message => message.payload.prototype === EmployeeToIssueAssignment
                && !!this._issue?.employeeToIssueAssignments.some(assignment => assignment.uid === message.payload.uid),
            method: () => this.requestUpdate(),
            disconnect: this._disconnected$
        });

        // Mark this card as deleted when its own issue is removed. We mark rather
        // than drop the row so the list never shifts under the user (the gap would
        // otherwise close upward by a whole card height); the row is gone on the
        // next reload. Styling targets the `is-deleted` class.
        this._messageBroker.registerListener<EntityDeletedPayload<Issue>>(this, {
            messageType: `entity.deleted`,
            filterFn: message => message.payload.entity instanceof Issue
                && message.payload.entity.uid === this.item?.uid,
            method: () => { this._deleted = true; },
            disconnect: this._disconnected$
        });

        this._initObservation();
    }

    protected render(): unknown {
        return html`
            ${until(this.data?.then(issue => {
                if (!issue) {
                    return html`
                        <div>Keine Daten vorhanden.</div>
                    `
                }
                return this._render(issue);
            }))}
        `;
    }

    protected _render(issue: Issue): unknown {
        this._issue = issue;
        const classMapping = {
            "is-in-past": issue.end < DateTime.utc().startOf(`day`),
            "is-first-of-future": this.isFirstOfFuture,
            "is-deleted": this._deleted,
            "ha-task-list__card-container": true,
        }

        return html`
            <div class=${classMap(classMapping)}>
                <div class="ha-task-list__card ${issue.status ? `ha-issue__status_${issue.status.key.toLowerCase().replace(' ', '-')}` : ''} ha-issue__type_${issue.type.shortcut.toLowerCase()}"
                     data-root-type=${rootUidOf(issue.type) ?? nothing}
                     style=${styleMap({"--issue-type-colory": issue.type.color, "--issue-type-text-color": resolveRecordTextColor(issue.type.color, issue.type.textColor)})}
                     @click="${this._onEditIssue}"
                >
                    <div class="ha-task-list__card-dates">
                        <div class="ha-task-list__card-content__date ${classMap({'out-of-order': this._isOutOfOrder(issue)})}">
                            ${this._renderTimeRange(issue.timeRange)}
                        </div>
                        <div class="ha-task-list__card-content__weeknumber">${issue.start.get(`weekNumber`)}. Woche
                        </div>
                    </div>
                    <div class="ha-task-list__card-content_status-box">
                        ${issue.status ? html`<div class="ha-task-list__card-content_status"
                             style=${styleMap(itemColorStyle(issue.status.color, issue.status.textColor))}>${issue.status.key}</div>` : nothing}
                    </div>
                    <div class="ha-task-list__card-content__title">${issue.title != '' ? issue.title : '-' }</div>
                    <div class="ha-task-list__card-content__description">
                        ${when(StringHelper.hasQuillContent(issue!.description), () => html`                        
                            <ha-quill-viewer .value=${issue!.description}></ha-quill-viewer>                        
                        `)}
                    </div>  
                    <div class="tasklist-details-wrapper">
                        <div class="ha-task-list__card-content__creator">
                            <div class="ha-task-list__column-header">${translate('general.creator')}</div>
                            ${issue.creator?.name ?? ``}
                        </div>
                        <div class="ha-task-list__card-content__employees ${classMap({empty: issue.employeeToIssueAssignments.length == 0})}">
                            <div class="ha-task-list__column-header">${translate('general.assignee')}</div>
                            <ul>
                                ${repeat(issue.employeeToIssueAssignments, assignment => assignment.employee!.uid, assignment => html`
                                    <li class="ha-task-list__employee-item">
                                        <span class="title">${until(assignment.employee!.then(e => e.name))}</span>
                                        <span class="timeBudget">${DateHelper.convertDurationToString(assignment.timeBudget)}</span>
                                    </li>
                                `)}
                                ${issue.employeeToIssueAssignments.length == 0 ? '-' : nothing}
                            </ul>
                        </div>
                        <div class="ha-task-list__card-content__project-daily ${classMap({empty: !issue.project?.title})}">
                            <div class="ha-task-list__column-header">${translate('general.daily-boards')} / ${translate('general.project-boards')}</div>
                            ${issue.project?.title ?? `-`}
                        </div>
                        <div class="ha-task-list__card-content__type">
                            <div class="ha-task-list__column-header">${translate('general.type')}</div>
                            ${issue.type.toString()}
                        </div>
                        <ul class="ha-task-list__keywords ${classMap({empty: issue.keywords.length == 0})}">
                            <div class="ha-task-list__column-header">${translate('general.label')}</div>                    
                            ${repeat(issue.keywords, keyword => keyword.uid, (keyword: Keyword) => html`
                                <li class="ha-task-list__keywords-item ha-${keyword.key.toLowerCase()}"
                                    style=${styleMap({backgroundColor: keyword.color, "--item-text-color": resolveRecordTextColor(keyword.color, keyword.textColor)})}>
                                    ${keyword.key}
                                </li>
                            `)}                               
                            ${issue.keywords.length == 0 ? '-' : nothing}
                        </ul>
                    </div>                                         
                </div>
            </div>
        `;
    }

    private _initObservation(): void {
        // Root MUST be the actual scrolling ancestor, not the (default) browser
        // viewport: `.ha-tasklist__issue-container` sits under a clipping chain
        // (the container itself has `contain: size layout`, and five ancestors above
        // it carry `overflow: hidden` up to <body>). Against the default viewport
        // root, a card's on-screen rectangle can satisfy the geometric intersection
        // check while still being clipped away by that chain — the observer then
        // never fires, `data` never gets set, and the card stays permanently blank
        // (measured: reproducible, independent of `threshold`/`rootMargin`; fixed by
        // pointing `root` at the real scrolling ancestor instead).
        //
        // Light DOM throughout this project (no shadow root to stop `closest()`), and
        // measured separately that the container is always already connected by the
        // time an item's connectedCallback runs (38/38 in a live run) — so this does
        // not race the container's own mount. `root: null` (falling back to the
        // viewport, today's pre-fix behaviour) is the deliberate fallback for an item
        // rendered outside this list; it must not throw or leave a worse case than
        // before this fix.
        const root = this.closest<HTMLElement>(`.ha-tasklist__issue-container`);

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.data = this._loadData();
                    observer.unobserve(this);
                }
            });
        }, {
            root,
            threshold: .01,
            // Generous preload margin so an item's data is fetched well before it
            // scrolls into view. Otherwise items appear empty (~70px) and grow to
            // their full height (~230px) once data arrives, shifting the scroll
            // anchor — especially noticeable when scrolling up into the past.
            //
            // With an explicit `root` this margin is now relative to the SCROLL
            // CONTAINER's own box, not the browser viewport as before the fix above —
            // a smaller reference box, but still generous enough to preload well
            // ahead of the visible area for this list's typical card height.
            rootMargin: `800px`
        });

        observer.observe(this);
    }

    private _loadData(): Promise<Issue | null> {
        if (!this.item) {
            return Promise.resolve(null);
        }

        return container.resolve(IssueRepository).findOneById(this.item.uid);
    }

    private async _onEditIssue(): Promise<void> {
        if (!!this.data) {
            const issue = await this.data;
            if (issue) {
                await IssueHelper.openIssueEditDialog(issue);
            }
        }
    }

    /**
     * Whether the issue's sort position is stale because its interval changed
     * after the list was last ordered. The list is sorted by `item.delta`
     * (computed server-side at load time, see TaskListQueryAction); recomputing
     * the delta from the live entity and comparing detects an interval edit that
     * has not been re-sorted yet. The next list reload refreshes `item.delta` and
     * clears the flag automatically.
     */
    private _isOutOfOrder(issue: Issue): boolean {
        if (!this.item) {
            return false;
        }

        const today = DateTime.utc().startOf(`day`);
        const start = issue.start.toUTC().startOf(`day`);
        const end = issue.end.toUTC().startOf(`day`);

        let currentDelta: number;
        if (start > today) {
            currentDelta = start.diff(today, `days`).days;           // future bucket
        } else if (end <= today) {
            currentDelta = end.diff(today, `days`).days - 1;         // past bucket
        } else {
            currentDelta = 0;                                        // today bucket
        }

        return Math.round(currentDelta) !== Math.round(this.item.delta);
    }

    private _renderTimeRange(timeRange: Interval): HTMLTemplateResult {
        const format = {day: `2-digit`, month: `2-digit`, year: `numeric`} as const;

        // Interval.end is exclusive (midnight of the following day), so the
        // inclusive last day is one day earlier — matching what the edit dialog
        // shows. Clamp to start so degenerate zero-length intervals (start == end)
        // never render an end that precedes the start.
        const start = timeRange.start!;
        const inclusiveEnd = DateTime.max(start, timeRange.end!.minus({days: 1}));

        return inclusiveEnd.hasSame(start, `day`)
            ? html`${start.toLocaleString(format)}`
            : html`${start.toLocaleString(format)} <span class="dash">&ndash;</span> ${inclusiveEnd.toLocaleString(format)}`;
    }
}